import React, { useId, useState } from 'react';
import { StickyFormBar } from '@/components/common';
import SecretInput from '@/components/ui/SecretInput';
import { ToggleField } from '@/components/ui';
import { STAFF_PLATFORM_ROLE_OPTIONS } from '@/utils/teamRoles';

const PASSWORD_HINT = 'At least 8 characters with one letter and one number.';

export type CreatePlatformUserPayload = {
  email: string;
  password: string;
  name: string;
  role: string;
  is_enabled: boolean;
};

type Props = {
  creating: boolean;
  onSubmit: (payload: CreatePlatformUserPayload) => Promise<void>;
};

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isValidPassword(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length >= 8 && /[A-Za-z]/.test(trimmed) && /\d/.test(trimmed);
}

export default function CreatePlatformUserForm({ creating, onSubmit }: Props) {
  const formId = useId();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('platform_support');
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!isValidEmail(email)) {
      setError('Enter a valid email address.');
      return;
    }
    if (!isValidPassword(password)) {
      setError(PASSWORD_HINT);
      return;
    }
    try {
      await onSubmit({
        email: email.trim(),
        password: password.trim(),
        name: name.trim() || email.trim().split('@')[0],
        role,
        is_enabled: enabled,
      });
      setEmail('');
      setName('');
      setPassword('');
      setRole('platform_support');
      setEnabled(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    }
  }

  return (
    <form id={formId} className="team-create-form team-create-form--polished" onSubmit={handleSubmit} autoComplete="off">
      <div className="team-create-form__sections">
        <section className="create-user-section card">
          <header className="create-user-section__header">
            <h3 className="create-user-section__title">Scoutly staff account</h3>
            <p className="muted">Platform users can access administration. They are not workspace members unless invited separately.</p>
          </header>
          <div className="create-user-section__body admin-form-grid admin-form-grid--team-create">
            <label>
              Email
              <input type="email" required autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label>
              Name
              <input value={name} autoComplete="off" placeholder="Optional display name" onChange={(e) => setName(e.target.value)} />
            </label>
            <label>
              Password
              <SecretInput value={password} autoComplete="new-password" onChange={setPassword} />
            </label>
            <label>
              Platform role
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                {STAFF_PLATFORM_ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <ToggleField checked={enabled} onChange={setEnabled} label="Account enabled" />
          </div>
        </section>
      </div>
      {error ? <div className="alert alert-error">{error}</div> : null}
      <StickyFormBar className="team-create-form__actions">
        <button type="submit" className="btn btn-primary" disabled={creating}>
          {creating ? 'Creating…' : 'Create platform user'}
        </button>
      </StickyFormBar>
    </form>
  );
}
